import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import InputField from "../atoms/FormField";
import Button from "../atoms/buttons";

const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, verifyOtp } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const redirectPath = location.state?.from?.pathname || "/dashboard";

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);

    try {
      if (!otpSent) {
        const result = await login({ email, password });
        if (result.requiresOtp) {
          setOtpSent(true);
          setInfo("OTP sent to your email. Please enter it below.");
          return;
        }
        navigate(redirectPath, { replace: true });
        return;
      }

      await verifyOtp({ email, otp });
      navigate(redirectPath, { replace: true });
    } catch (authError) {
      setError(authError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleLogin} className="space-y-6">
      {error && <div className="text-sm text-red-600">{error}</div>}
      {info && <div className="text-sm text-green-600">{info}</div>}
      <InputField
        type="email"
        name="email"
        placeholder="Enter your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        label="Email"
        autoComplete="email"
        required
      />
      <InputField
        type="password"
        name="password"
        placeholder="Enter your password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        label="Password"
        autoComplete="current-password"
        required
      />

      {otpSent && (
        <InputField
          type="text"
          name="otp"
          placeholder="Enter the OTP sent to your email"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          label="OTP Code"
          autoComplete="one-time-code"
          required
        />
      )}

      <div className="flex items-center justify-between pb-2">
        <div className="flex flex-col font-medium">
          <label className="flex cursor-pointer flex-row items-center">
            <span className="relative leading-none">
              <input
                type="checkbox"
                className="peer h-5 w-5 rounded border border-slate-300 bg-white text-slate-900 transition focus:ring-2 focus:ring-slate-900/20"
                name="rememberMe"
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="pointer-events-none absolute left-0 top-0 h-5 w-5 text-white opacity-0 peer-checked:opacity-100"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4.5 12.75l6 6 9-13.5"
                />
              </svg>
            </span>
            <span className="mb-0 ms-2 text-sm font-medium text-slate-700">
              Remember Me
            </span>
          </label>
        </div>
        <a
          className="h-auto p-0 text-sm font-semibold text-blue-600 underline transition-colors hover:text-slate-900 hover:no-underline"
          href="/auth/forgot-password-1"
        >
          Forgot password?
        </a>
      </div>

      <Button type="submit" className="w-full" disabled={loading}>
        <span>{loading ? "Processing..." : otpSent ? "Verify OTP" : "Sign in"}</span>
        <svg
          stroke="currentColor"
          fill="currentColor"
          strokeWidth="0"
          viewBox="0 0 256 256"
          className="ms-2 mt-0.5 h-5 w-5"
          height="1em"
          width="1em"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M224.49,136.49l-72,72a12,12,0,0,1-17-17L187,140H40a12,12,0,0,1,0-24H187L135.51,64.48a12,12,0,0,1,17-17l72,72A12,12,0,0,1,224.49,136.49Z" />
        </svg>
      </Button>
    </form>
  );
};

export default LoginForm;
